import type { CSSProperties } from "react";
import type { StatementDocData } from "../lib/api";
import { fmtAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";

// A statement of account to send a customer or vendor: opening balance, every entry in the period with a running
// balance, closing balance, and the bills still open. Wears the company's look (sql/224).
const TYPE: Record<string, string> = { sales: "Invoice", credit_note: "Credit note", receipt: "Payment received", payment: "Payment", purchase: "Bill",
  debit_note: "Debit note", journal: "Journal", contra: "Transfer" };

export function StatementDoc({ d }: { d: StatementDocData }) {
  const look = d.look ?? { template: "classic", accent: "#4338CA", paper: "A4", show_hsn_summary: false, show_pay_qr: false };
  const bal = (x: number) => `${fmtAmount(Math.abs(x))} ${x > 0 ? "Dr" : x < 0 ? "Cr" : ""}`;
  return (
    <article className={`doc t-${look.template} paper-${look.paper}`} style={{ "--doc-accent": look.accent } as CSSProperties}>
      <header className="doc-head">
        <div className="doc-from">
          {look.logo && <img className="doc-logo" src={look.logo} alt="" />}
          <div>
            <div className="doc-supplier">{d.company.name}</div>
            <div className="small">{[d.company.address, d.company.place, d.company.pincode].filter(Boolean).join(", ")}</div>
            {d.company.gstin && <div className="small">GSTIN <b className="mono">{d.company.gstin}</b></div>}
          </div>
        </div>
        <div className="doc-title"><h1>Statement of account</h1><div className="small muted">{fmtDate(d.from)} to {fmtDate(d.to)}</div></div>
      </header>
      <div className="doc-meta">
        <div className="doc-party">
          <div className="doc-label">Account of</div>
          <div className="doc-name">{d.party.name}</div>
          {d.party.address && <div>{d.party.address}</div>}
          {(d.party.place || d.party.pincode) && <div>{[d.party.place, d.party.pincode].filter(Boolean).join(" ")}</div>}
          {d.party.gstin && <div>GSTIN <b className="mono">{d.party.gstin}</b></div>}
        </div>
        <div className="doc-facts">
          <div><span>Opening balance</span><b>{bal(d.opening)}</b></div>
          <div><span>Debits</span><b>{fmtAmount(d.debits)}</b></div>
          <div><span>Credits</span><b>{fmtAmount(d.credits)}</b></div>
          <div><span>Closing balance</span><b>{bal(d.closing)}</b></div>
        </div>
      </div>
      <table className="doc-lines">
        <thead><tr><th>Date</th><th>Particulars</th><th>Number</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr></thead>
        <tbody>
          <tr className="muted"><td>{fmtDate(d.from)}</td><td>Opening balance</td><td /><td /><td /><td className="num">{bal(d.opening)}</td></tr>
          {d.lines.map((l, i) => (
            <tr key={i}><td>{fmtDate(l.date)}</td><td>{TYPE[l.type] ?? l.type}{l.particulars ? <div className="muted small">{l.particulars}</div> : null}</td>
              <td className="mono">{l.number}</td><td className="num">{l.debit ? fmtAmount(l.debit) : ""}</td><td className="num">{l.credit ? fmtAmount(l.credit) : ""}</td>
              <td className="num">{bal(l.balance)}</td></tr>))}
          <tr className="grand"><td colSpan={5}>Closing balance</td><td className="num">{bal(d.closing)}</td></tr>
        </tbody>
      </table>
      {d.open_bills.length > 0 && (<>
        <h3 className="doc-label" style={{ marginTop: 18 }}>Open bills on {fmtDate(d.to)}</h3>
        <table className="doc-lines">
          <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th className="num">Amount</th><th className="num">Still due</th><th className="num">Overdue</th></tr></thead>
          <tbody>{d.open_bills.map((b) => (
            <tr key={b.ref}><td className="mono">{b.ref}</td><td>{fmtDate(b.date)}</td><td>{b.due_date ? fmtDate(b.due_date) : ""}</td><td className="num">{fmtAmount(b.amount)}</td>
              <td className="num">{fmtAmount(b.pending)}</td><td className="num">{b.overdue_days ? `${b.overdue_days} days` : ""}</td></tr>))}</tbody>
        </table></>)}
      {look.bank && <div className="doc-pay"><div className="doc-bank"><div className="doc-label">Pay to</div>
        {look.bank.account_name && <div><b>{look.bank.account_name}</b></div>}
        {look.bank.account_no && <div>A/c no. <span className="mono">{look.bank.account_no}</span></div>}
        {look.bank.ifsc && <div>IFSC <span className="mono">{look.bank.ifsc}</span></div>}
        {look.upi_id && <div>UPI <span className="mono">{look.upi_id}</span></div>}</div></div>}
      <footer className="doc-foot"><div className="small muted">{look.footer ?? "This is a computer-generated statement."} Please tell us of any difference within 15 days.</div></footer>
    </article>
  );
}
